/**
 * Dev / opt-in timing for chat send → first token → stream end.
 * Enable: Vite dev, or VITE_CHAT_TIMING=true in .env
 */

export type ChatTimingLog = {
  type: "chat_timing";
  threadId: string | null;
  assistantId: string;
  model: string;
  /** Milliseconds from send_start (t0) */
  ms: Record<string, number>;
  /** Same as `ms.first_reasoning` when reasoning chunks occurred */
  first_reasoning_ms?: number;
};

export function isChatStreamTimingEnabled(): boolean {
  try {
    if (import.meta.env?.DEV) return true;
    if (import.meta.env?.VITE_CHAT_TIMING === "true") return true;
  } catch {
    /* non-Vite */
  }
  return false;
}

/** Milliseconds; 0 = off. Coalesces assistant text updates during stream. */
/** Default ~100ms coalesced UI updates during stream (set env to `0` to disable). */
const DEFAULT_STREAM_UI_THROTTLE_MS = 100;

export function getChatStreamUiThrottleMs(): number {
  try {
    const raw =
      import.meta.env?.VITE_CHAT_STREAM_UI_THROTTLE_MS ||
      import.meta.env?.VITE_UI_THROTTLE_MS;
    if (raw === undefined || raw === "") return DEFAULT_STREAM_UI_THROTTLE_MS;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : DEFAULT_STREAM_UI_THROTTLE_MS;
  } catch {
    return DEFAULT_STREAM_UI_THROTTLE_MS;
  }
}

import { getEffectiveStreamTimeoutMs } from "@/lib/chatStreamPreferences";

const DEFAULT_STREAM_TIMEOUT_MS = 300_000;

export function getChatStreamTimeoutMs(): number {
  try {
    const raw = import.meta.env?.VITE_CHAT_STREAM_TIMEOUT_MS;
    if (raw !== undefined && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) return n;
    }
  } catch {
    /* fall through */
  }
  try {
    return getEffectiveStreamTimeoutMs();
  } catch {
    return DEFAULT_STREAM_TIMEOUT_MS;
  }
}

export type ChatStreamTimingSession = {
  readonly t0: number;
  threadId: string | null;
  assistantId: string;
  model: string;
  readonly marks: Record<string, number>;
  setAssistantAndModel(assistantId: string, model: string): void;
  mark(phase: string): void;
  logSummary(extra?: Record<string, number>): void;
};

export function createChatStreamTimingSession(threadId: string | null): ChatStreamTimingSession | null {
  if (!isChatStreamTimingEnabled()) return null;

  const t0 = performance.now();
  const marks: Record<string, number> = { send_start: 0 };
  let assistantId = "";
  let model = "";

  const mark = (phase: string) => {
    marks[phase] = Math.round(performance.now() - t0);
  };

  const session: ChatStreamTimingSession = {
    t0,
    threadId,
    assistantId: "",
    model: "",
    marks,
    setAssistantAndModel(a: string, m: string) {
      session.assistantId = a;
      session.model = m;
    },
    mark,
    logSummary(extra?: Record<string, number>) {
      const ms = { ...marks, ...extra };
      const fr = ms.first_reasoning;
      const payload: ChatTimingLog = {
        type: "chat_timing",
        threadId: session.threadId,
        assistantId: session.assistantId,
        model: session.model,
        ms,
        ...(typeof fr === "number" ? { first_reasoning_ms: fr } : {}),
      };
      console.log(JSON.stringify(payload));
    },
  };
  return session;
}
